import React from 'react';
import { Link } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { motion } from 'framer-motion';

const LandingFooter = () => {
  const { isMobile } = useIsMobile();

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  return (
    <footer className="relative overflow-hidden bg-transparent border-t border-gray-200 dark:border-slate-800/50">
      {/* Animated background elements - only in dark mode */}
      <div className="absolute top-0 left-0 w-full h-full dark:block hidden">
        <div className="absolute top-10 left-10 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-indigo-500/10 rounded-full blur-2xl animate-pulse delay-500"></div>
      </div>

      <div className={`relative z-10 ${isMobile ? 'py-8 px-4' : 'py-12 px-4 sm:px-6 lg:px-8'}`}>
        <div className="max-w-screen-2xl mx-auto">
          {/* Main Content */}
          <motion.div
            className={`grid grid-cols-1 md:grid-cols-4 ${isMobile ? 'gap-6' : 'gap-8'}`}
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {/* Brand Section */}
            <motion.div className="md:col-span-1" variants={fadeInUp}>
              <div className="flex items-center space-x-2 mb-4">
                <img src="/synkro.png" alt="Synkro" className="h-8 w-8" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  Synkro
                </h3>
              </div>
              <p className="text-gray-600 dark:text-slate-300 leading-relaxed mb-4 max-w-md text-sm">
                Revolutionizing research with AI-powered tools and seamless collaboration.
              </p>
            </motion.div>

            {/* Product Section */}
            <motion.div variants={fadeInUp}>
              <h4 className="font-semibold text-lg text-gray-900 dark:text-white mb-4">Product</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/features"
                    className="text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white transition-colors duration-300 text-sm"
                  >
                    Features
                  </Link>
                </li>
                <li>
                  <Link
                    to="/integrations"
                    className="text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white transition-colors duration-300 text-sm"
                  >
                    Integrations
                  </Link>
                </li>
                <li>
                  <Link
                    to="/pricing"
                    className="text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white transition-colors duration-300 text-sm"
                  >
                    Pricing
                  </Link>
                </li>
              </ul>
            </motion.div>

            {/* Company Section */}
            <motion.div variants={fadeInUp}>
              <h4 className="font-semibold text-lg text-gray-900 dark:text-white mb-4">Company</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/about"
                    className="text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white transition-colors duration-300 text-sm"
                  >
                    About Us
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white transition-colors duration-300 text-sm"
                  >
                    Contact
                  </Link>
                </li>
              </ul>
            </motion.div>

            {/* Legal Section */}
            <motion.div variants={fadeInUp}>
              <h4 className="font-semibold text-lg text-gray-900 dark:text-white mb-4">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/privacy"
                    className="text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white transition-colors duration-300 text-sm"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className="text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white transition-colors duration-300 text-sm"
                  >
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link
                    to="/data-deletion"
                    className="text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white transition-colors duration-300 text-sm"
                  >
                    Data Deletion
                  </Link>
                </li>
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </footer>
  );
};

export default LandingFooter; 